import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  OlHTMLAttributes,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { moveTo } from 'ui-lib'
import styled from 'styled-components'
import { Colors, Icon } from '@blueprintjs/core'

import DragNDropProvider, {
  DragEffectCallback,
} from './drag-n-drop/drag-n-drop-provider'
import DragGroupEffect from './drag-n-drop/drag-group-effect'
import EnableDragAndDrop from './drag-n-drop/enable-drag-and-drop'
import { DropzoneDragGroupIds } from './drag-n-drop/should-accept-drop'
import {
  DragNDropReturn,
  useDragNDrop,
} from './drag-n-drop/hooks/use-drag-n-drop'

type RenderItem<T> = (
  item: T,
  dragNDropReturn: DragNDropReturn,
) => ReactElement | null

type LiProps = DetailedHTMLProps<HTMLAttributes<HTMLLIElement>, HTMLLIElement>

export type ComputeLiProps<T> = (
  item: T,
  dragNDropReturn: DragNDropReturn,
) => Omit<LiProps, 'draggable' | 'ref'>

const StyledOl = styled.ol<{
  $columnCount: number
}>`
  float: left;
  column-count: ${(props) => props.$columnCount};
  padding-left: 0;
`

const StyledLi = styled.li`
  list-style: inside decimal;
  margin: 5px;
  width: 180px;
`

const StyledDiv = styled.div<{
  isDraggable: boolean
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  &:hover {
    cursor: ${(props) => (props.isDraggable ? 'move' : 'default')};
    background-color: ${(props) => (props.isDraggable ? Colors.GRAY1 : 'none')};
  }
`
const SimpleDraggableLI = <T,>({
  item,
  dragGroupIds,
  dropzoneDragGroupIds,
  renderItem,
  computeLiProps,
}: {
  item: T
  dragGroupIds: string[]
  dropzoneDragGroupIds: DropzoneDragGroupIds
  renderItem?: RenderItem<T>
  computeLiProps: ComputeLiProps<T>
}) => {
  const elemRef = useRef<HTMLDivElement>(null)
  const dragNDropReturn = useDragNDrop(
    elemRef,
    dragGroupIds,
    dropzoneDragGroupIds,
  )
  const isDraggable = dragNDropReturn.isDraggable
  const divProps = computeLiProps?.(item, dragNDropReturn)
  return (
    <StyledDiv
      ref={elemRef}
      data-id={item}
      className={divProps.className}
      draggable={isDraggable}
      isDraggable
    >
      <Icon icon="drag-handle-vertical" />
      <StyledLi>{renderItem?.(item, dragNDropReturn) ?? null}</StyledLi>
    </StyledDiv>
  )
}

const ReorderableList = <T,>({
  listId,
  items,
  setItems,
  getId,
  getElemId,
  renderItem,
  olProps,
  computeLiProps,
  maxItemsPerColumn = 10,
}: {
  listId: string
  items: T[]
  setItems: (newItems: T[]) => void
  getId: (item: T) => string
  getElemId: (elem: HTMLElement | undefined) => string | undefined
  renderItem: RenderItem<T>
  olProps?: Omit<
    DetailedHTMLProps<OlHTMLAttributes<HTMLOListElement>, HTMLOListElement>,
    'ref'
  >
  computeLiProps: ComputeLiProps<T>
  maxItemsPerColumn?: number
}) => {
  const swap: DragEffectCallback = useCallback(
    (event, draggedElem, dropzoneElem) => {
      if (event.type !== 'drop') {
        return
      }
      const draggedIndex = items.findIndex(
        (item) => getId(item) === getElemId(draggedElem),
      )
      const dropzoneIndex = items.findIndex(
        (item) => getId(item) === getElemId(dropzoneElem),
      )
      if (
        draggedIndex === -1 ||
        dropzoneIndex === -1 ||
        draggedIndex === dropzoneIndex
      ) {
        return
      }
      setItems(moveTo(items, draggedIndex, dropzoneIndex))
    },
    [items, setItems, getId, getElemId],
  )
  const groupIds = useMemo(() => [listId], [listId])
  const columnCount = Math.max(
    1,
    Math.ceil(items.length / Math.max(1, maxItemsPerColumn)),
  )
  return (
    <DragNDropProvider>
      <EnableDragAndDrop dragGroupIds={groupIds} />
      <DragGroupEffect
        dragEffectGroupIds={groupIds}
        dragEffectCallback={swap}
      />
      <StyledOl {...olProps} $columnCount={columnCount}>
        {items.map((item) => (
          <SimpleDraggableLI<T>
            key={getId(item)}
            item={item}
            computeLiProps={computeLiProps}
            dragGroupIds={groupIds}
            dropzoneDragGroupIds={groupIds}
            renderItem={renderItem}
          />
        ))}
      </StyledOl>
    </DragNDropProvider>
  )
}

export default ReorderableList
